import { createSlice } from '@reduxjs/toolkit';
import apiClient from '@miroculus/api-client';
import { updateWorkspaces } from '../workspaces';
import { toast } from 'react-toastify';
import navigationService from '../../navigation/navigationService';
import { DASHBOARD_URL } from '../../constants/routes';
const initialState = {
    organizations: [],
    teams: [],
    loadingOrganizations: false,
    currentOrganization: {},
    sendingInvitation: false,
    updatingOrganization: false,
    pendingInvites: [],
    inviteLink: null,
    deletingTeam: false,
};
const organizationsSlice = createSlice({
    name: 'organizations',
    initialState,
    reducers: {
        updateLoadingOrganizations: (state, action) => {
            state.loadingOrganizations = action.payload;
        },
        updateOrganizations: (state, action) => {
            state.organizations = action.payload;
        },
        updateCurrentOrganization: (state, action) => {
            state.currentOrganization = action.payload;
        },
        updateOrganizationMembers: (state, action) => {
            state.currentOrganization.members = action.payload;
        },
        updateOrganizationTeams: (state, action) => {
            const teams = action.payload;
            const currentOrgIndex = state.organizations.findIndex((org) => org.slug === state.currentOrganization.slug);
            if (currentOrgIndex !== -1) {
                state.organizations[currentOrgIndex].teams = teams;
            }
            state.currentOrganization.teams = teams;
        },
        updateDeletingTeam: (state, action) => {
            state.deletingTeam = action.payload;
        },
        updateSendingInvitation: (state, action) => {
            state.sendingInvitation = action.payload;
        },
        updateUpdatingOrganization: (state, action) => {
            state.updatingOrganization = action.payload;
        },
        updatePendingInvites: (state, action) => {
            state.pendingInvites = action.payload;
        },
        updateInviteLink: (state, action) => {
            state.inviteLink = action.payload;
        },
    },
});
// Export actions
export const { updateLoadingOrganizations, updateOrganizations, updateCurrentOrganization, updateOrganizationMembers, updateOrganizationTeams, updateDeletingTeam, updateSendingInvitation, updateUpdatingOrganization, updatePendingInvites, updateInviteLink, } = organizationsSlice.actions;
// Thunk Actions
export const loadOrganizations = () => async (dispatch) => {
    dispatch(updateLoadingOrganizations(true));
    try {
        const { body: organizations } = await apiClient.get('/organizations');
        dispatch(updateOrganizations(organizations));
        dispatch(updateWorkspaces(organizations.flatMap((o) => o.teams)));
    }
    catch (e) {
        console.error(e);
    }
    dispatch(updateLoadingOrganizations(false));
};
export const switchToOrganization = (orgSlug) => async (dispatch) => {
    dispatch(updateLoadingOrganizations(true));
    try {
        const { body: currentOrganization } = await apiClient.get(`/organizations/${orgSlug}`);
        dispatch(updateCurrentOrganization(currentOrganization));
    }
    catch (e) {
        console.error(e);
    }
    finally {
        dispatch(updateLoadingOrganizations(false));
    }
};
export const inviteUserToOrganization = (email, role, orgSlug) => async (dispatch) => {
    dispatch(updateSendingInvitation(true));
    try {
        await apiClient.post(`/organization-invites/${orgSlug}`, { role, email });
        toast.success('The invitation to collaborate in this organization was sent successfully');
    }
    catch (e) {
        toast.error(`Error inviting new member: "${e.message}"`);
        throw e;
    }
    finally {
        dispatch(updateSendingInvitation(false));
    }
};
export const getInviteLinkToOrganization = (email, role, orgSlug) => async (dispatch) => {
    dispatch(updateSendingInvitation(true));
    try {
        const { res } = await apiClient.post(`/organization-invites/${orgSlug}/link`, { role, email });
        dispatch(updateInviteLink(await res.text()));
        toast.success('The invitation link has been generated successfully.');
    }
    catch (e) {
        toast.error(`Error inviting new member: "${e.message}"`);
        throw e;
    }
    finally {
        dispatch(updateSendingInvitation(false));
    }
};
export const redeemInvitation = (inviteToken, inviteEmail) => async (_, getState) => {
    const { email } = getState().auth.user;
    if (email !== inviteEmail) {
        toast.error('The token you are trying to redeem is for another user, please log out and try again', { autoClose: false });
        return navigationService.push(DASHBOARD_URL);
    }
    try {
        const { body: organization } = await apiClient.post('/organization-invites/redeem', { inviteToken });
        toast.success(`You are now a member of '${organization.name}' organization`);
    }
    catch (e) {
        toast.error(`There was a problem trying to redeem your invitation. Please ask for another invitation. (${e.message})`, { autoClose: false });
        navigationService.push(DASHBOARD_URL);
    }
};
export const removeInvite = (id) => async () => {
    await apiClient.del(`/organization-invites/${id}`);
    toast.success('The invitation was removed successfully');
};
export const loadPendingInvites = () => async (dispatch) => {
    try {
        const { body: invitations } = await apiClient.get('/organization-invites');
        dispatch(updatePendingInvites(invitations.filter((i) => !i.accepted)));
    }
    catch (e) {
        console.error(e);
    }
};
export const loadOrganizationMembers = (organizationSlug) => async (dispatch) => {
    dispatch(updateLoadingOrganizations(true));
    try {
        const { body: organizationMembers } = await apiClient.get(`/organization-members/${organizationSlug}`);
        dispatch(updateOrganizationMembers(organizationMembers.map(({ id, role, user: { name, email }, createdAt, active }) => ({
            id,
            name,
            role,
            email,
            createdAt,
            active,
        }))));
    }
    catch (e) {
        console.error(e);
    }
    finally {
        dispatch(updateLoadingOrganizations(false));
    }
};
export const createOrganization = (name, token) => () => apiClient.post('/organizations', { name, token });
export const updateOrganization = (orgSlug, { name }) => async (dispatch, getState) => {
    await apiClient.put(`/organizations/${orgSlug}`, { name });
    const { currentOrganization, organizations } = getState().organizations;
    const updatedOrganizations = organizations.map((o) => o.slug === orgSlug ? { ...o, name } : o);
    const updatedCurrentOrganization = { ...currentOrganization, name };
    dispatch(updateOrganizations(updatedOrganizations));
    dispatch(updateCurrentOrganization(updatedCurrentOrganization));
};
export const updateOrganizationMember = ({ memberId, ...values }) => async (dispatch, getState) => {
    dispatch(updateLoadingOrganizations(true));
    try {
        await apiClient.put(`/organization-members/${memberId}`, values);
        const { members } = getState().organizations.currentOrganization;
        if (!members)
            return;
        const memberIndex = members.findIndex((m) => m.id === memberId);
        const membersUpdated = members.slice();
        membersUpdated[memberIndex] = { ...members[memberIndex], ...values };
        dispatch(updateOrganizationMembers(membersUpdated));
        toast.success('The member info was updated successfully');
    }
    catch (e) {
        console.error(e);
        toast.error('Something went wrong updating this organization member');
    }
    finally {
        dispatch(updateLoadingOrganizations(false));
    }
};
export const deleteOrganizationMember = (memberId) => async (dispatch, getState) => {
    dispatch(updateLoadingOrganizations(true));
    try {
        await apiClient.del(`/organization-members/${memberId}`);
        const { members } = getState().organizations.currentOrganization;
        if (!members)
            return;
        const memberIndex = members.findIndex((m) => m.id === memberId);
        dispatch(updateOrganizationMembers([
            ...members.slice(0, memberIndex),
            ...members.slice(memberIndex + 1),
        ]));
        toast.success('The organization member has been deleted successfully');
    }
    catch (e) {
        console.error(e);
        toast.error('Something went wrong trying to delete the organization member');
    }
    finally {
        dispatch(updateLoadingOrganizations(false));
    }
};
export const createUpdateOrganizationTeam = ({ teamId, organization, title }) => async (dispatch, getState) => {
    dispatch(updateLoadingOrganizations(true));
    try {
        const { body: newTeam } = await apiClient.post(teamId ? `/teams/${teamId}` : '/teams', teamId ? { title } : { organization, title });
        const { teams } = getState().organizations.currentOrganization;
        if (!teams)
            return;
        if (teamId) {
            const teamIndex = teams.findIndex((m) => m.id === teamId);
            const teamsUpdated = teams.slice();
            teamsUpdated[teamIndex] = { ...teams[teamIndex], title };
            dispatch(updateOrganizationTeams(teamsUpdated));
            dispatch(updateWorkspaces(teamsUpdated));
            toast.success('The team info was updated successfully');
        }
        else {
            // new team has one memberCount when is created (the user that has created the team)
            const teamsUpdated = [...teams, { ...newTeam, $memberCount: 1 }];
            dispatch(updateOrganizationTeams(teamsUpdated));
            dispatch(updateWorkspaces(teamsUpdated));
            toast.success('The team was created successfully');
        }
    }
    catch (e) {
        console.error(e);
        toast.error('Something went wrong.');
    }
    finally {
        dispatch(updateLoadingOrganizations(false));
    }
};
export const deleteOrganizationTeam = ({ teamId, organization }) => async (dispatch, getState) => {
    dispatch(updateDeletingTeam(true));
    try {
        await apiClient.del(`/teams/${teamId}`);
        const { teams } = getState().organizations.currentOrganization;
        if (!teams)
            return;
        console.log('teamId', teamId);
        console.log('teams', teams);
        const teamIndex = teams.findIndex((m) => m.id === teamId);
        dispatch(updateOrganizationTeams([
            ...teams.slice(0, teamIndex),
            ...teams.slice(teamIndex + 1),
        ]));
        dispatch(updateWorkspaces(teams.filter((t) => t.id !== teamId)));
        toast.success('The team was deleted successfully');
    }
    catch (e) {
        console.error(e);
        toast.error('Something went wrong trying to delete the team');
    }
    finally {
        dispatch(updateDeletingTeam(false));
    }
};
export const leaveOrganization = (memberId) => async (dispatch, getState) => {
    dispatch(updateLoadingOrganizations(true));
    try {
        await apiClient.del(`/organization-members/${memberId}`);
        const { organizations, currentOrganization } = getState().organizations;
        dispatch(updateCurrentOrganization({}));
        const updatedOrganizations = organizations.filter((o) => o.slug !== currentOrganization.slug);
        dispatch(updateOrganizations(updatedOrganizations));
        navigationService.push(DASHBOARD_URL);
        toast.success(`You have left the organization ${currentOrganization.name}`);
    }
    catch (e) {
        console.error(e);
        if (e.message.includes('You cannot delete the last admin of the organization')) {
            toast.error('You cannot leave the organization because you are the last admin. Please assign another admin before leaving.');
        }
        else {
            toast.error('Something went wrong trying to leave the organization');
        }
    }
    finally {
        dispatch(updateLoadingOrganizations(false));
    }
};
// Export reducer as default
export default organizationsSlice.reducer;
